import Linked, { useLinked } from "@linked/react/lib";
import { styled } from "@material-ui/core";
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import { useIO, useModel } from "@type-r/react";
import { Button, CollapsableSection, Flex, FormLabel, Loading, PopupDialog, PrimaryButton, div } from "lqx-react";
import React from "react";
import { AssetFilterCriteria, WorkSheet } from "../../../api/assetFilterCriteria";
import { AssetType } from "../../../api/assetType";
import { AssetTypeItem } from "../../../api/assetTypes";
import { DocumentSource } from "../../../api/documentSource";
import { ImportTemplateModel } from "../../../api/importTemplateModel";
import { IngestionJob } from "../../../api/ingestionJob";
import { TargetFieldCard } from "../../../api/targetFieldCard";
import { DigiCheckbox, preventDefaultAndStopPropagation } from "../../../shared";
import { ComputedFieldItem, MappingItem, StaticFieldItem } from "../../models/import-template-details";
import { importTemplate } from "../../models/import-wizard-constants";
import { FileReaderView } from "../import-template-mapper/file-reader-view";
import { FilterCriteriaSection } from "./filter-criteria-section";
import { SelectOption } from "./select-option";
import { StandardSelectOption } from "./standard-select-option";
import { StaticSelectOption } from "./static-select-option";
import { StaticSelectionPopup } from "./static-selection-popup";
import { ComputedSelectionPopup } from "./computed-selection-popup";
import { ComputedSelectOption } from "./computed-select-option";

export const FileMapperFields = ({
    isStandard,
    file,
    mappingItems,
    staticFieldItems,
    computedFieldItems,
    inBlockMappingFields,
    inBlockStaticFields,
    inBlockComputedFields,
    onSelect,
    onSelectStatic,
    onSelectComputed,
    onDelete,
    onDeleteStatic,
    onDeleteComputed,
    onClose,
    onCheck,
    onSelectMultipleSource,
    omitUnmappedFields,
    onFormattingSelect,
    assetTypes,
    $currentAssetType,
    selectColumnMode,
    assetFilterCriteria,
    deleteColumnFilter,
    workSheet }
    : {
        isStandard: boolean,
        file: any,
        mappingItems?: MappingItem[],
        staticFieldItems?: StaticFieldItem[],
        computedFieldItems?: ComputedFieldItem[],
        inBlockMappingFields?: TargetFieldCard[],
        inBlockStaticFields?: TargetFieldCard[],
        inBlockComputedFields?: TargetFieldCard[],
        onSelect?: any,
        onSelectStatic?: any,
        onSelectComputed?: any,
        onDelete?: any,
        onDeleteStatic?: any,
        onDeleteComputed?: any,
        onClose?: any,
        onCheck?: any,
        onSelectMultipleSource?: any,
        omitUnmappedFields, onFormattingSelect: any,
        assetTypes: AssetTypeItem[],
        $currentAssetType: Linked<AssetTypeItem>
        selectColumnMode?: any,
        assetFilterCriteria: AssetFilterCriteria,
        deleteColumnFilter: any,
        workSheet: WorkSheet
    }) => {
    const $expand = useLinked(false);
    const parentRef = React.createRef<any>();
    const $isPopupOpen = useLinked(false);
    const onClosePopup = () => $isPopupOpen.set(false);
    const $isStaticPopupOpen = useLinked(false);
    const hideStaticPopup = () => $isStaticPopupOpen.set(false);
    const showStaticPopup = () => $isStaticPopupOpen.set(true);
    const $isComputedPopupOpen = useLinked(false);
    const hideComputedPopup = () => $isComputedPopupOpen.set(false);
    const showComputedPopup = () => $isComputedPopupOpen.set(true);
    const $hideRunTemplate = useLinked(true);
    const ingestionJob = useModel(IngestionJob);
    const importTemplateModel = useModel(ImportTemplateModel);
    const $data = useLinked([[]]);
    const $doParseData = useLinked<boolean>(false);
    const toggleModelView = () => $expand.value ? colapseModelView() : expandModelView();
    const expandParent = () => document.getElementById('expandContainer');
    const collapseParent = () => document.getElementById('collapseContainer');
    const checkActive = (type: string) => type === $currentAssetType.value.name;
    const getSelectOptionUniqueKey = (item: any, index: number) => item.from.name + '_' + (item.from.x + item.from.y + +item.to.id) + +index;
    const getStaticSelectOptionUniqueKey = (item: any) => item.fieldName + '_' + item.value;
    const maxWidthHandler = (index) => assetTypes.length - 1 === index && assetTypes.length % 2 !== 0 ? '92%' : '41%';
    const assetTypeName = assetTypes.length > 1 ? $currentAssetType.value.name : '';

    const clearAll = () => {
        omitUnmappedFields.set(false);
        onClosePopup();
        onClose();
    };

    const onStaticSelectionApply = (feildName: string, value: string, id: number) => {
        $isStaticPopupOpen.set(false);
        onSelectStatic(
            feildName,
            value,
            id);
    };

    const onComputedSelectionApply = (field1: number, field2: number, operator: string, resultField: number) => {
        $isComputedPopupOpen.set(false);
        onSelectComputed(
            field1,
            field2,
            operator,
            resultField
        );
    };

    const capitalizeMappingObjectProperties = (mappings) => {
        const objectToArray = (entries) => {
            var res = {};
            for (var i = 0; i < entries.length; i++) res[entries[i][0]] = entries[i][1];
            return res;
        }

        const transformObjectKeys = (fn) => (obj) => objectToArray(
            Object.entries(obj).map(([k, v]) => [
                fn(k),
                v
                    ? Array.isArray(v) ? v.map(transformObjectKeys(fn)) : typeof v == 'object' ? transformObjectKeys(fn)(v) : v
                    : typeof v == 'boolean' ? Boolean(v) : null
            ])
        )
        const capitalizeString = (s: string) => s[0].toUpperCase() + s.slice(1);
        return mappings.map((m) => transformObjectKeys(capitalizeString)(m));
    }

    useIO(async () => {
        try {
            $doParseData.value && await parseData();
        } finally {
            $doParseData.set(false);
        }
    }, [$doParseData.value])

    const parseData = async () => {
        $data.set(null)
        $hideRunTemplate.set(false);

        const getUserTemplate = () => {
            const items = capitalizeMappingObjectProperties(mappingItems);
            return [{ AssetTypeId: $currentAssetType.value.id, MappingItems: items }];
        };

        const getStaticFields = () => {
            const items = capitalizeMappingObjectProperties(staticFieldItems);
            return [{ AssetTypeId: $currentAssetType.value.id, StaticFieldItems: items }];
        };

        let sourceId;
        let fileCode;
        let sourceName;

        if (+$currentAssetType.value.id === AssetType.invoice) {
            sourceId = DocumentSource.Invoice.sourceId;
            fileCode = DocumentSource.Invoice.fileCode;
            sourceName = DocumentSource.Invoice.sourceName;
        }
        if (+$currentAssetType.value.id === AssetType.creditNote) {
            sourceId = DocumentSource.CreditNote.sourceId;
            fileCode = DocumentSource.CreditNote.fileCode;
            sourceName = DocumentSource.CreditNote.sourceName;
        }
        if (+$currentAssetType.value.id === AssetType.remittanceAdvice) {
            sourceId = DocumentSource.RemittanceAdvice.sourceId;
            fileCode = DocumentSource.RemittanceAdvice.fileCode;
            sourceName = DocumentSource.RemittanceAdvice.sourceName;
        }
        if (+$currentAssetType.value.id === AssetType.unappliedCash) {
            sourceId = DocumentSource.UnappliedCash.sourceId;
            fileCode = DocumentSource.UnappliedCash.fileCode;
            sourceName = DocumentSource.UnappliedCash.sourceName;
        }
        if (+$currentAssetType.value.id === AssetType.customerReferenceData) {
            sourceId = DocumentSource.CustomerReferenceData.sourceId;
            fileCode = DocumentSource.CustomerReferenceData.fileCode;
            sourceName = DocumentSource.CustomerReferenceData.sourceName;
        }
        if (+$currentAssetType.value.id === AssetType.payment) {
            sourceId = DocumentSource.Payment.sourceId;
            fileCode = DocumentSource.Payment.fileCode;
            sourceName = DocumentSource.Payment.sourceName;
        }

        await ingestionJob.enqueue({
            params: {
                file: file,
                sourceId: sourceId,
                sourceName: sourceName,
                fileCode: fileCode,
                userTemplate: JSON.stringify(getUserTemplate()),
                staticFields: JSON.stringify(getStaticFields()),
                assetFilterCriteria: assetFilterCriteria,
                omitUnmappedFields: omitUnmappedFields.value,
                workSheet: workSheet
            }
        });

        ingestionJob.reset();

        const originalFilename = ingestionJob.fileName.split('.');
        const newFilename = `${originalFilename[0]}.json`;

        await importTemplateModel.getParsedFile({
            params: {
                name: newFilename
            }
        }, mappingItems);

        $data.set(importTemplateModel.parsedData as any);
    }

    const expandModelView = () => {
        $expand.set(true);
        const parent = expandParent()
        if (parent) {
            parent.appendChild(parentRef.current);
        }
    }
    const colapseModelView = () => {
        $expand.set(false);
        const parent = collapseParent()
        if (parent) {
            parent.appendChild(parentRef.current);
        }
    }

    const handleAssetType = (type) => () => {
        $currentAssetType.set(type);
        $hideRunTemplate.set(true);
    }

    return (
        <>
            <Flex column stretch>
                {isStandard ? <></> :
                    <>
                        <CollapsableSection title={`${assetTypeName} Unmapped Fields`} id="unmapped-fields" >
                            <StyledRow>
                                <FormLabel>Omit Unmapped fields</FormLabel>
                                <DigiCheckbox $checked={omitUnmappedFields} readOnly={!mappingItems.length}></DigiCheckbox>
                            </StyledRow>
                        </CollapsableSection>
                    </>
                }
                {assetTypes.length > 1 ?
                    <FilterCriteriaSection
                        selectColumnMode={selectColumnMode}
                        assetFilterCriteria={assetFilterCriteria}
                        deleteColumnFilter={deleteColumnFilter} /> : <></>}
                <Flex wrap>
                    {assetTypes.length > 1 && assetTypes.map((item, index) => (
                        <InBlockButton
                            key={index}
                            fullWidth
                            $width={maxWidthHandler(index)}
                            $focus={checkActive(item.name)}
                            onClick={handleAssetType(item)}
                        >
                            {item.name}
                        </InBlockButton>
                    ))}
                </Flex>
                <CollapsableSection title={`${assetTypeName} Mapped Fields (${mappingItems.length})`} id="mapped-fields" right={
                    mappingItems.length > 0 && <Button fullWidth={false} size="small" onClick={() => $isPopupOpen.set(true)}>Clear All</Button>
                }>
                    <Flex row>
                        <SourceFileColumn>Source File</SourceFileColumn>
                        {isStandard ? <></> :
                            <FormattingColumn>Formatting</FormattingColumn>
                        }
                        <InBlockFieldColumn>Field</InBlockFieldColumn>
                        {isStandard ? <></> :
                            <GlobalFieldColumn>Global Field</GlobalFieldColumn>
                        }
                    </Flex>
                    {
                        isStandard
                            ?
                            mappingItems.map((item, index) => {
                                return (
                                    <StandardSelectOption key={getSelectOptionUniqueKey(item, index)} mappingItem={item} />
                                );
                            })
                            :
                            mappingItems.sort((a, b) => a.from.x - b.from.x).map((item, index) => {
                                const notFilteredInBlockMappingFields = inBlockMappingFields.filter((inBlockMappingField) =>
                                    mappingItems.every((mapping) => mapping.to.id !== inBlockMappingField.id || mapping.to.id === importTemplate.selectOption.id) &&
                                    staticFieldItems.every((staticField) => staticField.fieldName !== inBlockMappingField.name));
                                return (
                                    <SelectOption key={getSelectOptionUniqueKey(item, index)}
                                        mappingItem={item}
                                        inBlockMappingFields={item.to.id === importTemplate.selectOption.id ?
                                            notFilteredInBlockMappingFields :
                                            [...notFilteredInBlockMappingFields, inBlockMappingFields.find((x) => x.id === item.to.id)].filter(i => i)}
                                        onSelect={onSelect}
                                        onDelete={onDelete}
                                        onCheck={onCheck}
                                        onSelectMultipleSource={onSelectMultipleSource}
                                        onFormattingSelect={onFormattingSelect} />
                                );
                            })
                    }
                </CollapsableSection>
                <CollapsableSection title={`${assetTypeName} Static Fields (${staticFieldItems.length})`} id="static-fields" right={
                    <PrimaryButton onClick={(e) => {
                        preventDefaultAndStopPropagation(e);
                        showStaticPopup();
                    }}>Add static field</PrimaryButton>
                }>
                    <Flex row>
                        <InBlockFieldColumn>Field</InBlockFieldColumn>
                        <StaticValueColumn>Static Value</StaticValueColumn>
                    </Flex>
                    {
                        staticFieldItems.map((item) => {
                            const notFilteredInBlockMappingFields = inBlockMappingFields.filter((inBlockMappingField) =>
                                mappingItems.every((mapping) => mapping.to.id !== inBlockMappingField.id) &&
                                staticFieldItems.every((staticField) => staticField.fieldName !== inBlockMappingField.name) &&
                                inBlockMappingField.id !== importTemplate.selectOption.id);
                            return (
                                <StaticSelectOption key={getStaticSelectOptionUniqueKey(item)}
                                    staticFieldItem={item}
                                    inBlockStaticFields={[...notFilteredInBlockMappingFields, inBlockMappingFields.find((x) => x.name === item.fieldName)].filter(i => i)}
                                    onSelectStatic={onSelectStatic} onDeleteStatic={onDeleteStatic} />
                            );
                        })
                    }

                </CollapsableSection>
                {
                    assetTypes.length === 1 && assetTypes[0].name === 'Invoice' &&
                    <CollapsableSection title={`${assetTypeName} Computed Fields (${computedFieldItems.length})`} id="computed-fields" right={
                        <PrimaryButton onClick={(e) => {
                            preventDefaultAndStopPropagation(e);
                            showComputedPopup();
                        }
                        }>Add computed field</PrimaryButton>
                    }>
                        <Flex row>
                            <ComputedFieldHeaderColumn>Field 1</ComputedFieldHeaderColumn>
                            <OperatorComputedFieldHeaderColumn>Operator</OperatorComputedFieldHeaderColumn>
                            <ComputedFieldHeaderColumn>Field 2</ComputedFieldHeaderColumn>
                            <ComputedFieldHeaderColumn>Result Field</ComputedFieldHeaderColumn>
                        </Flex>
                        {
                            computedFieldItems.map((item) => {
                                return (
                                    <ComputedSelectOption computedFieldItem={item} onDeleteComputed={onDeleteComputed} />
                                );
                            })
                        }

                    </CollapsableSection>
                }
                {isStandard ? <></> :
                    <div id="collapseContainer">
                        <div ref={parentRef}>
                            <CollapsableSection title={`${assetTypeName} Line Items`} id="invoice-line-items" right={
                                <>
                                    <FullscreenIcon onClick={(e) => { e.stopPropagation(); toggleModelView(); }} />
                                    <PrimaryButton disabled={$doParseData.value} onClick={(e) => { e.stopPropagation(); $doParseData.set(true); }}>Run Template</PrimaryButton>
                                </>
                            }>
                                <ModelPreviewContainer hidden={$hideRunTemplate.value}>
                                    <FileReaderViewContainer
                                        $data={$data}
                                        mappingItems={mappingItems}
                                        inBlockMappingFields={inBlockMappingFields}
                                        isInProgress={$doParseData.value}
                                    />
                                </ModelPreviewContainer>
                            </CollapsableSection>
                        </div>
                    </div>
                }
            </Flex>
            {$isPopupOpen.value && (
                <PopupDialog
                    title='CLEAR ALL'
                    actions={
                        <>
                            <Button onClick={onClosePopup}>
                                Cancel
                            </Button>
                            <PrimaryButton onClick={clearAll}>
                                Clear All
                            </PrimaryButton>
                        </>
                    }
                    maxWidth='sm'
                    hideTitle={false}
                    onClose={onClosePopup}
                >
                    Clear all the selections that have been made.
                </PopupDialog>
            )}
            {
                $isStaticPopupOpen.value && (
                    <StaticSelectionPopup
                        onStaticSelectionApply={onStaticSelectionApply}
                        onClose={hideStaticPopup}
                        inBlockStaticFields={inBlockStaticFields}
                    />
                )
            }
            {
                $isComputedPopupOpen.value && (
                    <ComputedSelectionPopup
                        onComputedSelectionApply={onComputedSelectionApply}
                        onClose={hideComputedPopup}
                        inBlockComputedFields={inBlockComputedFields}
                        resultFields={inBlockMappingFields.filter(x => x.id !== '0')}
                    />
                )
            }
        </>
    );
};

const FileReaderViewContainer = ({ $data, mappingItems, inBlockMappingFields, isInProgress }) => {
    let result: any = <FailedToRunTitle>Failed to run template</FailedToRunTitle>;
    const hasData = () => Boolean($data.value) && typeof ($data.value[0][0]) !== 'function';

    const getInBlockMappingFields = () => {
        return inBlockMappingFields.filter((inBlockMappingField) =>
            mappingItems.every((mapping) => mapping.to.id !== inBlockMappingField.id) && inBlockMappingField.id !== importTemplate.selectOption.id);
    }

    if (hasData() && !isInProgress) {
        result = <FileReaderView
            csvData={$data.value}
            mappingItems={mappingItems}
            inBlockMappingFields={getInBlockMappingFields()}
            isViewMode={true}
            isRunImportTemplateMode={true}
        />
    } else if (isInProgress) {
        result = <Loading />;
    }

    return result;
}

interface IButtonComponent {
    $focus?: boolean,
    $width?: string | number,
}

const InBlockButton = styled(({ $focus, $width, ...rest }) => <Button {...rest} />)({
    backgroundColor: (props: IButtonComponent) => props.$focus && '#163C66',
    color: (props: IButtonComponent) => props.$focus && 'white',
    margin: '10px 5px 10px 5px',
    width: '100%',
    maxWidth: (props: IButtonComponent) => props.$width,
});

export const FailedToRunTitle = styled('div')({
    color: '#767D84',
    fontFamily: 'Montserrat',
    fontSize: 12,
    fontWeight: 500,
    marginTop: '50px',
    textAlign: 'center'
})

const ModelPreviewContainer = div({
    display: 'flex',
    flexDirection: 'column'
})

const FieldHeaderColumn = div({
    display: 'flex',
    flexDirection: 'column',
    color: '#668AA5',
    fontSize: '14px',
    fontWeight: 600
})

const SourceFileColumn = styled(FieldHeaderColumn)({
    flex: '0 0 130px'
})

const FormattingColumn = styled(FieldHeaderColumn)({
    flex: '0 0 120px',
})

const StaticValueColumn = styled(FieldHeaderColumn)({
    flex: '0 0 265px'
})

const InBlockFieldColumn = styled(FieldHeaderColumn)({
    flex: 1
})

const GlobalFieldColumn = styled(FieldHeaderColumn)({
    flex: '0 0 90px'
})

const ComputedFieldHeaderColumn = styled(FieldHeaderColumn)({
    flex: '0 0 115px'
})

const OperatorComputedFieldHeaderColumn = styled(FieldHeaderColumn)({
    flex: '0 0 100px'
})

const StyledRow = div({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
})

export const AddStaticFieldButton = styled(PrimaryButton)({
    position: 'absolute',
    top: '8px',
    right: '30px'
});